import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToasterService } from '../../providers/toaster/toaster.service';
import { MasterDataService } from './master-data.service';
import { CourseService } from '../../pages/courses/course.service';
import { PaginationComponent } from '../pagination/pagination.component';
import {
  requiredMinMaxValidator,
  requiredCourseIdValidator,
  requiredFileValidator
} from '../../utils/certificate_validators/certificate-validators';

@Component({
  selector: 'app-master-data',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PaginationComponent],
  templateUrl: './master-data.component.html',
  styleUrl: './master-data.component.css'
})
export class MasterDataComponent implements OnInit {
  courseOptions: Array<{ id: any; course_name: string }> = [];
  courseLoading = false;
  private courseFullyLoaded = false;
  // Resolvers waiting for courses to finish loading
  private courseLoadedResolvers: Array<() => void> = [];

  activeModal: 'certificate' | 'badge' | null = null;
  records: any[] = [];
  paginatedRecords: any[] = [];
  loadingRecords = false;
  loadingEdit = false;
  saving = false;
  currentPage = 1;
  readonly pageSize = 10;

  // Validation constraints (min/max)
  readonly MAX_TITLE = 120;
  readonly MIN_TITLE = 2;
  readonly MAX_CERT_TYPE = 80;
  readonly MIN_CERT_TYPE = 2;
  readonly MAX_DATE_TYPE = 80;
  readonly MIN_DATE_TYPE = 2;

  readonly MAX_DESCRIPTION = 500;
  readonly MIN_DESCRIPTION = 2;

  // Reactive forms
  certificateFormGroup: FormGroup;
  badgeFormGroup: FormGroup;

  // Track which record (if any) is being edited
  certificateEditId: number | null = null;
  badgeEditId: number | null = null;

  // Flip to true the moment Save is clicked so ALL errors show immediately,
  // even for fields the user never touched/blurred.
  certificateSubmitted = false;
  badgeSubmitted = false;

  constructor(
    private fb: FormBuilder,
    private masterDataService: MasterDataService,
    private toasterService: ToasterService,
    private courseService: CourseService
  ) {
    this.certificateFormGroup = this.fb.group({
      course_id: ['', requiredCourseIdValidator()],
      title: ['', requiredMinMaxValidator({ required: true, min: this.MIN_TITLE, max: this.MAX_TITLE })],
      certificate_type: [
        '',
        requiredMinMaxValidator({ required: true, min: this.MIN_CERT_TYPE, max: this.MAX_CERT_TYPE })
      ],
      date_type: [
        '',
        requiredMinMaxValidator({ required: true, min: this.MIN_DATE_TYPE, max: this.MAX_DATE_TYPE })
      ],
      signature: [null, requiredFileValidator()]
    });

    this.badgeFormGroup = this.fb.group({
      course_id: ['', requiredCourseIdValidator()],
      title: ['', requiredMinMaxValidator({ required: true, min: this.MIN_TITLE, max: this.MAX_TITLE })],
      description: [
        '',
        requiredMinMaxValidator({ required: true, min: this.MIN_DESCRIPTION, max: this.MAX_DESCRIPTION })
      ]
    });
  }

  ngOnInit(): void {
    this.fetchRecords();
    this.loadAllCoursesOnce();
  }

  // ─── Returns a promise that resolves once courseOptions is fully populated ─
  private whenCoursesReady(): Promise<void> {
    if (this.courseFullyLoaded) return Promise.resolve();
    return new Promise((resolve) => {
      this.courseLoadedResolvers.push(resolve);
    });
  }

  private loadAllCoursesOnce(): void {
    if (this.courseFullyLoaded || this.courseLoading) return;

    this.courseLoading = true;
    const all: Array<{ id: any; course_name: string }> = [];

    const extractItems = (res: any): any[] => {
      const body = res?.body;
      const data = res?.data;
      let candidate = body?.data ?? data ?? body;
      if (candidate && typeof candidate === 'object' && Array.isArray((candidate as any).data)) {
        candidate = (candidate as any).data;
      }
      return Array.isArray(candidate) ? candidate : [];
    };

    const loadPage = (page: number): Promise<void> =>
      new Promise((resolve) => {
        this.courseService.getAllCourses(page).subscribe({
          next: (res: any) => {
            const items = extractItems(res);
            all.push(
              ...items.map((c: any) => ({
                id: c?.id != null ? String(c.id) : null,
                course_name: c?.course_name ?? c?.name ?? '-'
              }))
            );
            resolve(items.length > 0 ? loadPage(page + 1) : undefined);
          },
          error: () => resolve()
        });
      });

    loadPage(1)
      .then(() => {
        const seen = new Set<any>();
        const uniq: Array<{ id: any; course_name: string }> = [];
        for (const c of all) {
          if (c?.id == null || seen.has(c.id)) continue;
          seen.add(c.id);
          uniq.push(c);
        }
        this.courseOptions = uniq;
        this.courseFullyLoaded = true;

        // Notify all callers waiting on whenCoursesReady()
        this.courseLoadedResolvers.forEach((r) => r());
        this.courseLoadedResolvers = [];
      })
      .catch(() => {
        this.courseOptions = [];
        this.courseLoadedResolvers.forEach((r) => r());
        this.courseLoadedResolvers = [];
      })
      .finally(() => {
        this.courseLoading = false;
      });
  }

  openModal(type: 'certificate' | 'badge'): void {
    if (type === 'certificate') this.resetCertificateForm();
    else this.resetBadgeForm();
    this.activeModal = type;
  }

  closeModal(): void {
    this.activeModal = null;
    this.loadingEdit = false;
    this.resetCertificateForm();
    this.resetBadgeForm();
  }

  fetchRecords(): void {
    this.loadingRecords = true;
    this.masterDataService.getAllCertificates().subscribe({
      next: (certRes: any) => {
        const certList = certRes?.body?.data || certRes?.data || [];
        this.masterDataService.getAllBadges().subscribe({
          next: (badgeRes: any) => {
            const badgeList = badgeRes?.body?.data || badgeRes?.data || [];
            this.records = [
              ...this.normalizeRecords(certList, 'certificate'),
              ...this.normalizeRecords(badgeList, 'badge')
            ];
            this.currentPage = 1;
            this.updatePaginatedRecords();
            this.loadingRecords = false;
          },
          error: () => {
            this.loadingRecords = false;
            this.toasterService.setToaster('error', 'Error', 'Failed to load badges');
          }
        });
      },
      error: () => {
        this.loadingRecords = false;
        this.toasterService.setToaster('error', 'Error', 'Failed to load certificates');
      }
    });
  }

  normalizeRecords(items: any[], type: 'certificate' | 'badge') {
    return (items || []).map((item) => ({
      id: item?.id,
      title: item?.title ?? item?.certificate_name ?? item?.badge_name ?? item?.name ?? 'Untitled',
      description: item?.description || '',
      course_name:
        item?.course_name ??
        item?.course?.course_name ??
        this.getCourseNameById(item?.course_id) ??
        '-',
      typeLabel: type === 'certificate' ? 'Certificate' : 'Badge',
      record_type: type,
      raw: item
    }));
  }

  private extractResponseData(res: any): any {
    let data = res?.body?.data ?? res?.data ?? res?.body ?? res ?? {};

    if (data && typeof data === 'object' && !Array.isArray(data) && 'data' in data) {
      data = data.data;
    }

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      data = data.certificate ?? data.badge ?? data;
    }

    return Array.isArray(data) ? (data[0] ?? {}) : data;
  }

  private extractCourseId(raw: any): string {
    const candidate =
      raw?.course_id ?? raw?.course?.id ?? raw?.course?.course_id ?? raw?.course?.courseId ?? raw?.course_id?.id;
    return candidate != null && candidate !== '' ? String(candidate) : '';
  }

  private resolveCourseId(raw: any): string {
    const directId = this.extractCourseId(raw);
    if (directId && this.courseOptions.some((course) => String(course.id) === directId)) {
      return directId;
    }

    const rawCourseName = String(raw?.course_name ?? raw?.course?.course_name ?? '').trim().toLowerCase();
    if (!rawCourseName) {
      return '';
    }

    const matchedCourse = this.courseOptions.find(
      (course) => String(course.course_name ?? '').trim().toLowerCase() === rawCourseName
    );
    return matchedCourse ? String(matchedCourse.id) : '';
  }

  private ensureCourseOptionForEdit(raw: any, resolvedCourseId: string): void {
    if (!resolvedCourseId) {
      return;
    }

    const exists = this.courseOptions.some((course) => String(course.id) === resolvedCourseId);
    if (exists) {
      return;
    }

    const fallbackName = String(raw?.course_name ?? raw?.course?.course_name ?? '').trim();
    this.courseOptions = [
      ...this.courseOptions,
      {
        id: resolvedCourseId,
        course_name: fallbackName || `Course ${resolvedCourseId}`
      }
    ];
  }

  getCourseNameById(courseId: any): string | null {
    const match = this.courseOptions.find((c) => String(c.id) === String(courseId));
    return match?.course_name || null;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.records.length / this.pageSize));
  }

  get paginationData(): {
    prev_page: number | null;
    next_page: number | null;
    current_page: number;
    pages: number[];
  } {
    const prev_page = this.currentPage > 1 ? this.currentPage - 1 : null;
    const next_page = this.currentPage < this.totalPages ? this.currentPage + 1 : null;
    const pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);

    return {
      prev_page,
      next_page,
      current_page: this.currentPage,
      pages
    };
  }

  private updatePaginatedRecords(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedRecords = this.records.slice(start, end);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.updatePaginatedRecords();
  }

  previousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  onSignatureSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] || null;
    const control = this.certificateFormGroup.get('signature');
    control?.setValue(file);
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  /**
   * Central error-message resolver used by the template. Returns null when
   * there's nothing to show (control valid, or not yet touched/submitted).
   */
  getError(form: FormGroup, field: string, submitted: boolean): string | null {
    const control = form.get(field);
    if (!control || !control.errors) return null;
    if (!(control.touched || submitted)) return null;

    if (control.errors['required'] || control.errors['courseRequired']) {
      return 'This field is required';
    }
    if (control.errors['minLength']) {
      return `Must be at least ${control.errors['minLength'].requiredMin} characters`;
    }
    if (control.errors['maxLength']) {
      return `Must be at most ${control.errors['maxLength'].requiredMax} characters`;
    }
    if (control.errors['fileExtension']) {
      return `Allowed file types: ${control.errors['fileExtension'].allowed.join(', ')}`;
    }
    if (control.errors['fileSizeMax']) {
      return 'File is too large';
    }
    return null;
  }

  saveCertificate(): void {
    this.certificateSubmitted = true;
    this.certificateFormGroup.markAllAsTouched();

    if (this.certificateFormGroup.invalid) {
      this.saving = false;
      return;
    }

    this.saving = true;

    const formValue = this.certificateFormGroup.value;
    const courseName = this.getCourseNameById(formValue.course_id) || '';
    const payload: any = {
      type: 'certificate',
      course_id: formValue.course_id,
      course_name: courseName,
      certificate_name: formValue.title,
      certificate_type: formValue.certificate_type,
      date_type: formValue.date_type,
      is_active: 1
    };

    let request$;
    if (formValue.signature instanceof File) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== null && value !== undefined) formData.append(key, value as string);
      });
      formData.append('certificate_file', formValue.signature);
      request$ = this.certificateEditId
        ? this.masterDataService.updateCertificate(this.certificateEditId, formData)
        : this.masterDataService.createCertificate(formData);
    } else {
      request$ = this.certificateEditId
        ? this.masterDataService.updateCertificate(this.certificateEditId, payload)
        : this.masterDataService.createCertificate(payload);
    }

    request$.subscribe({
      next: (res: any) => {
        this.saving = false;

        const body = res.body ?? res;

        if (body.status === 'success') {
          this.toasterService.setToaster('success', 'Success', body.message || 'Certificate saved successfully');
          this.closeModal();
          this.fetchRecords();
        } else {
          this.toasterService.setToaster('error', 'Error', body.message || 'Failed to save certificate');
        }
      },
      error: (err) => {
        this.saving = false;
        this.toasterService.setToaster('error', 'Error', err?.error?.message || 'Failed to save certificate');
      }
    });
  }

  saveBadge(): void {
    this.badgeSubmitted = true;
    this.badgeFormGroup.markAllAsTouched();

    if (this.badgeFormGroup.invalid) {
      this.saving = false;
      return;
    }

    this.saving = true;

    const formValue = this.badgeFormGroup.value;
    const courseName = this.getCourseNameById(formValue.course_id) || '';
    const payload = {
      type: 'badge',
      course_id: formValue.course_id,
      course_name: courseName,
      badge_name: formValue.title,
      description: formValue.description,
      is_active: 1
    };

    const request$ = this.badgeEditId
      ? this.masterDataService.updateBadge(this.badgeEditId, payload)
      : this.masterDataService.createBadge(payload);

    request$.subscribe({
      next: (res: any) => {
        this.saving = false;

        const body = res.body ?? res;

        if (body.status === 'success') {
          this.toasterService.setToaster('success', 'Success', body.message || 'Badge saved successfully');
          this.closeModal();
          this.fetchRecords();
        } else {
          this.toasterService.setToaster('error', 'Error', body.message || 'Failed to save badge');
        }
      },
      error: (err) => {
        this.saving = false;
        this.toasterService.setToaster('error', 'Error', err?.error?.message || 'Failed to save badge');
      }
    });
  }

  openEdit(record: any): void {
    if (!record) return;

    if (record.record_type === 'certificate') {
      this.activeModal = 'certificate';
      this.resetCertificateForm();
      this.certificateEditId = record.id;
      this.loadingEdit = true;

      this.masterDataService.getCertificateById(record.id).subscribe({
        next: (res: any) => {
          const raw = this.extractResponseData(res);

          // Wait for courseOptions to be ready before setting course_id
          this.whenCoursesReady().then(() => {
            const resolvedCourseId = this.resolveCourseId(raw) || this.extractCourseId(raw);
            this.ensureCourseOptionForEdit(raw, resolvedCourseId);

            // Map API response -> form fields.
            // Backend keys may differ, so we try several common possibilities.
            const signatureValue =
              raw?.certificate_file ?? raw?.signature_file ?? raw?.certificate_signature_file ?? null;

            this.certificateFormGroup.patchValue({
              course_id: resolvedCourseId,
              title: raw?.title ?? raw?.certificate_name ?? raw?.certificate_title ?? '',
              certificate_type: raw?.certificate_type ?? raw?.type_of_certificate ?? raw?.certificate_type_name ?? '',
              date_type: raw?.date_type ?? raw?.date ?? raw?.date_type_name ?? '',
              signature: signatureValue ? ({ name: String(signatureValue) } as any) : null
            });
            this.loadingEdit = false;
          });
        },
        error: () => {
          this.loadingEdit = false;
          this.toasterService.setToaster('error', 'Error', 'Failed to load certificate details');
        }
      });
    } else {
      this.activeModal = 'badge';
      this.resetBadgeForm();
      this.badgeEditId = record.id;
      this.loadingEdit = true;

      this.masterDataService.getBadgeById(record.id).subscribe({
        next: (res: any) => {
          const raw = this.extractResponseData(res);

          // Wait for courseOptions before setting course_id
          this.whenCoursesReady().then(() => {
            const resolvedCourseId = this.resolveCourseId(raw) || this.extractCourseId(raw);
            this.ensureCourseOptionForEdit(raw, resolvedCourseId);
            this.badgeFormGroup.patchValue({
              course_id: resolvedCourseId,
              title: raw?.title ?? raw?.badge_name ?? raw?.badge_title ?? '',
              description: raw?.description ?? raw?.badge_description ?? ''
            });
            this.loadingEdit = false;
          });
        },
        error: () => {
          this.loadingEdit = false;
          this.toasterService.setToaster('error', 'Error', 'Failed to load badge details');
        }
      });
    }
  }

  deleteRecord(record: any): void {
    if (!record?.id) return;
    const delete$ =
      record.record_type === 'certificate'
        ? this.masterDataService.deleteCertificate(record.id)
        : this.masterDataService.deleteBadge(record.id);
    delete$.subscribe({
      next: () => {
        this.toasterService.setToaster('success', 'Deleted', 'Record deleted');
        this.fetchRecords();
      },
      error: () => {
        this.toasterService.setToaster('error', 'Error', 'Failed to delete record');
      }
    });
  }

  private resetCertificateForm(): void {
    this.certificateEditId = null;
    this.certificateSubmitted = false;
    this.certificateFormGroup.reset({
      course_id: '',
      title: '',
      certificate_type: '',
      date_type: '',
      signature: null
    });
  }

  private resetBadgeForm(): void {
    this.badgeEditId = null;
    this.badgeSubmitted = false;
    this.badgeFormGroup.reset({
      course_id: '',
      title: '',
      description: ''
    });
  }
}